import * as React from "react"

import { cn } from '../../cn'

const cardVariants = {
  default: 'bg-card border border-border shadow-card rounded-[var(--card-radius)]',
  glass: 'bg-card/80 backdrop-blur-sm border border-border/50 shadow-card rounded-[var(--card-radius)]',
  outline: 'bg-transparent border-2 border-border rounded-[var(--card-radius)]',
} as const

const cardSizes = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
} as const

const cardInteractive = {
  none: '',
  clickable: 'cursor-pointer transition-shadow hover:shadow-md hover:border-accent/40',
  draggable: 'cursor-grab active:cursor-grabbing transition-shadow hover:shadow-md hover:border-accent/40 active:shadow-lg active:scale-[1.02]',
} as const

export type CardVariant = keyof typeof cardVariants
export type CardSize = keyof typeof cardSizes
export type CardInteractive = keyof typeof cardInteractive

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant
  size?: CardSize
  interactive?: CardInteractive
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, size, interactive, ...props }, ref) => (
    <div
      data-component="Card"
      data-package="@appic/ui-components"
      ref={ref}
      role="article"
      className={cn(
        variant ? cardVariants[variant] : "foundation-card",
        "text-card-foreground",
        "min-w-0 max-w-full overflow-hidden",
        size && cardSizes[size],
        interactive && cardInteractive[interactive],
        className
      )}
      {...props}
    />
  )
)
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col gap-3 p-6 min-w-0", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "font-semibold leading-none tracking-tight",
      "break-words min-w-0",
      className
    )}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "text-sm text-muted-foreground",
      "break-words min-w-0",
      className
    )}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("p-6 pt-0 min-w-0 overflow-x-auto", className)}
    {...props}
  />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0 min-w-0 gap-3", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
